import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from 'react';
import React from 'react';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage 
} from '../../utils/facilityStorage';
import { getYardAndFacilityData, getYardAndWarehouseForModals, getWarehouseData, GRAPHQL_API_URL } from './api';

import { getAuthToken } from '../../utils/cookieUtils';
// Types for yard spots and activities
export type ScheduledTime = {
  startTime: Date;
  endTime: Date;
  driverId: string | null;
  driverName: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  shipmentNumber: string | null;
};

export type Status = 'available' | 'in-use' | 'inactive' | 'loaded' | 'empty' | 'unavailable';

export type Zone = {
  id: string;
  name: string;
  color: string;
  zone_type?: string;
};

export type YardSpot = {
  id: string;
  number: number;
  displayNumber: string;
  zoneId: string;
  status: Status;
  driverId: string | null;
  driverName: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  trailerType?: 'dry_van' | 'reefer' | 'flatbed' | 'tanker' | 'container';
  shipmentNumber: string | null;
  bol_id?: string | null;
  carrier_name?: string | null;
  scheduledTimes?: ScheduledTime[];
  current_status?: string | null;
  direction?: string | null;
  order_number?: number | null;
  trailerStatus?: string | null; // LOADED, EMPTY, etc.
  notes?: string; // Notes from configuration
  active_lineage?: {
    uuid: string;
    notes?: string;
    created_at?: string;
  };
  data?: any; // Add data field for trailer data including OOS status
};

export type HistoryEntry = {
  id: string;
  timestamp: Date;
  driverName: string;
  activity: 'loading' | 'unloading' | 'parked' | 'maintenance';
  duration: number;
  details?: string;
};

export type YardHistory = {
  [spotId: string]: HistoryEntry[];
};

// Custom hook to fetch yard data
export const useYardData = (facilityId?: number, refreshInterval: number = 30000, zoneFilter?: string | null) => {
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Core state for data
  const [yardSpots, setYardSpots] = useState<YardSpot[]>([]);
  const [yardHistory, setYardHistory] = useState<YardHistory>({});
  const [facilities, setFacilities] = useState<{ id: number; name: string }[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<{ id: number; name: string } | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });
  const [timeInterval, setTimeInterval] = useState('24');
  const [zones, setZones] = useState<Zone[]>([]);
  const [facilityZones, setFacilityZones] = useState<any[]>([]);
  const [allZones, setAllZones] = useState<Zone[]>([]); // Full unfiltered zones for modals
  const [allFacilityZones, setAllFacilityZones] = useState<any[]>([]); // Full unfiltered facility zones for modals
  const [allYardSpots, setAllYardSpots] = useState<YardSpot[]>([]); // Full unfiltered yard spots for trailer search
  const [warehouseData, setWarehouseData] = useState<any>(null); // Warehouse data for dock modals

  // Lock-based state for tracking locked spots and trailers
  const [taskBasedLockedSpots, setTaskBasedLockedSpots] = useState<Set<string>>(new Set());
  const [taskBasedLockedTrailers, setTaskBasedLockedTrailers] = useState<Set<string>>(new Set());
  const [activeLocks, setActiveLocks] = useState<any[]>([]);
  const [allFacilityLocks, setAllFacilityLocks] = useState<any[]>([]);

  // Use refs for tracking fetching state without causing renders
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch facilities only once on mount (separate from consolidated query)
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        // GraphQL query for facilities only
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, clear it and set default
            if (facilityId) {
              const facility = result.data.facilities.find((f: any) => f.id === facilityId);
              if (facility) {
                setSelectedFacility(facility);
                saveFacilityToStorage(facility);
              }
            } else if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
              saveFacilityToStorage(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (facilityId) {
            const facility = result.data.facilities.find((f: any) => f.id === facilityId);
            if (facility) {
              setSelectedFacility(facility);
              saveFacilityToStorage(facility);
            }
          } else if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
            saveFacilityToStorage(result.data.facilities[0]);
          }
        }
      } catch (err) {
        setError('Failed to fetch facilities');
        console.error(err);
      }
    };
    
    fetchFacilities();
  }, [facilityId]);

  // Initialize base yard spots when facility changes
  useEffect(() => {
    if (!selectedFacility) {
      setYardSpots([]);
      return;
    }
    
    // Initialize with empty array, will be populated by consolidated GraphQL query
    setYardSpots([]);
  }, [selectedFacility]);

  // Consolidated data fetching function used for both initial load and refresh
  const fetchConsolidatedData = useCallback(async (facilityZoneUuid?: string, silent: boolean = false) => {
    if (!selectedFacility) return;
    
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Fetch yard and facility data using the API service
      const result = await getYardAndFacilityData(
        selectedFacility.id.toString(),
        facilityZoneUuid
      );

      // Debug: Log the consolidated GraphQL response
      console.log("Consolidated GraphQL Response (Yard + Facility + Locks):", result);
      
      // Process the yard data
      if (result && result.yard) {
        const yardData = result.yard;
        
        // Extract and process zones from the GraphQL response
        if (yardData.facility_zones && yardData.facility_zones.length > 0) {
          // Build the zones from GraphQL data
          const graphQLZones = yardData.facility_zones.map((zone: any) => ({
            id: zone.facility_zone_uuid,
            name: zone.facility_zone_name,
            color: zone.configuration?.color || 
                   (zone.zone_type === "staging" ? "#F59E0B" : "#2196F3"), // Use config color, or amber for staging, or blue as default
            zone_type: zone.zone_type
          }));

          // remove "Main Yard" from the zones
          const filteredZones = graphQLZones.filter((zone: any) => zone.name !== "Main Yard");
          
          setZones(filteredZones);
          
          // Store all zones if this is a full (unfiltered) load for use in modals
          if (!facilityZoneUuid) {
            setAllZones(filteredZones);
            setAllFacilityZones(yardData.facility_zones);
          }
          
          // Process all yard spots from all zones
          const allSpots: YardSpot[] = [];
          
          yardData.facility_zones.forEach((zone: any) => {
            if (zone.yard_spots && zone.yard_spots.length > 0) {
              zone.yard_spots.forEach((spot: any) => {
                // Determine spot status - prioritize configuration status first
                let status: Status = 'available';
                
                // Treat configuration "unavailable" as an override; treat "available" as unset/unknown
                const configStatus = spot.configuration?.status;
                if (configStatus === 'unavailable') {
                  status = 'unavailable';
                } else if (spot.yard_spot_status === 'INACTIVE' || spot.yard_spot_status === 'inactive') {
                  status = 'inactive';
                } else if (spot.trailer && spot.trailer.status && spot.trailer.status.name) {
                  // Use trailer status to determine display
                  if (spot.trailer.status.name === 'LOADED') {
                    status = 'loaded'; // Blue tile
                  } else if (spot.trailer.status.name === 'EMPTY') {
                    status = 'empty'; // Yellow tile
                  } else {
                    status = 'in-use'; // Fallback for other trailer statuses
                  }
                } else if (spot.trailer_uuid) {
                  // Has trailer but no status - fallback to in-use
                  status = 'in-use';
                }
                
                // Determine direction - ONLY use shipment direction if available
                let direction: string | null = null;
                
                if (spot.shipment && spot.shipment.direction) {
                  direction = spot.shipment.direction;
                }
                
                // Map API spot to our internal YardSpot type
                allSpots.push({
                  id: spot.yard_spot_uuid,
                  number: parseInt(spot.yard_spot_name.replace(/\D/g, '')) || 0,
                  displayNumber: spot.yard_spot_name,
                  zoneId: zone.facility_zone_uuid,
                  status,
                  driverId: spot.driver_id || null,
                  driverName: spot.driver_name || null,
                  trailerId: spot.trailer_uuid || null,
                  trailerNumber: spot.trailer_name || null,
                  shipmentNumber: spot.shipment_reference_number || null,
                  bol_id: spot.shipment_id || null,
                  carrier_name: spot.carrier_name || null,
                  current_status: spot.yard_spot_status || null,
                  direction,
                  order_number: spot.order_number ? parseInt(spot.order_number, 10) : null,
                  trailerStatus: spot.trailer?.status?.name || null,
                  notes: spot.configuration?.notes || null, // Extract notes from configuration
                  active_lineage: spot.trailer?.active_lineage ? {
                    uuid: spot.trailer.active_lineage.uuid,
                    notes: spot.trailer.active_lineage.notes,
                    created_at: spot.trailer.active_lineage.created_at
                  } : undefined,
                  data: spot.trailer?.data || null, // Include trailer data for OOS access
                  scheduledTimes: [] // Initialize with empty scheduledTimes array
                });
              });
            }
          });
          
          // Sort spots within each zone by order_number
          const sortedSpots = allSpots.sort((a, b) => {
            // First sort by zone
            if (a.zoneId !== b.zoneId) {
              return a.zoneId.localeCompare(b.zoneId);
            }
            // Then sort by order_number within the same zone
            // Handle null/undefined values by treating them as very high numbers (put them at the end)
            const aOrderNumber = (a.order_number !== null && a.order_number !== undefined) ? a.order_number : Number.MAX_SAFE_INTEGER;
            const bOrderNumber = (b.order_number !== null && b.order_number !== undefined) ? b.order_number : Number.MAX_SAFE_INTEGER;
            return aOrderNumber - bOrderNumber;
          });
          
          setYardSpots(sortedSpots);
          
          // Store all yard spots if this is a full (unfiltered) load for use in modals
          if (!facilityZoneUuid) {
            setAllYardSpots(sortedSpots);
          }
          
          // Store facility zones data for zone-based trailer access
          setFacilityZones(yardData.facility_zones);
        } else {
          // No yard data found
          setZones([]);
          setYardSpots([]);
          setFacilityZones([]);
        }
      } else {
        // No yard data found
        setZones([]);
        setYardSpots([]);
        setFacilityZones([]);
      }

      // Process facility locks to determine locked yard spots and trailers
      if (result && result.facilityLocks) {
        const locks = result.facilityLocks;
        const lockedSpotIds = new Set<string>();
        const lockedTrailerIds = new Set<string>();
        const activeLocks: any[] = [];
        
        // Store all facility locks for use by other components
        setAllFacilityLocks(locks);

        locks.forEach((lock: any) => {
          // Check if this lock applies to a yard spot
          const isYardSpotLock = lock.model_name === 'YardSpot' || 
                                lock.model_name === 'YardSpotObject' || 
                                (lock.resource && lock.resource.__typename === 'YardSpotObject');
          
          // Check if this lock applies to a trailer
          const isTrailerLock = lock.model_name === 'Trailer' || 
                               (lock.resource && lock.resource.__typename === 'Trailer');
          
          if (isYardSpotLock) {
            // Store the lock information for linking to tasks
            activeLocks.push(lock);
            
            // Add the yard spot UUID to locked spots
            const spotUuid = lock.resource_uuid || lock.resource?.uuid;
            if (spotUuid) {
              lockedSpotIds.add(spotUuid);
            }
          } else if (isTrailerLock) {
            // Store the lock information for linking to tasks
            activeLocks.push(lock);
            
            // Add the trailer UUID to locked trailers
            const trailerUuid = lock.resource_uuid || lock.resource?.uuid;
            if (trailerUuid) {
              lockedTrailerIds.add(trailerUuid);
            }
          }
        });

        setTaskBasedLockedSpots(lockedSpotIds);
        setTaskBasedLockedTrailers(lockedTrailerIds);
        setActiveLocks(activeLocks);
      } else {
        setTaskBasedLockedSpots(new Set());
        setTaskBasedLockedTrailers(new Set());
        setActiveLocks([]);
        setAllFacilityLocks([]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch consolidated data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedFacility]);

  // Background function to load full unfiltered data for modals
  const fetchBackgroundData = useCallback(async () => {
    if (!selectedFacility) return;
    
    try {
      // Fetch full yard and warehouse data in a single consolidated query using API service
      const result = await getYardAndWarehouseForModals(selectedFacility.id.toString());

      // Store full zones data
      if (result?.yard?.facility_zones) {
        const graphQLZones = result.yard.facility_zones.map((zone: any) => ({
          id: zone.facility_zone_uuid,
          name: zone.facility_zone_name,
          color: zone.configuration?.color || (zone.zone_type === "staging" ? "#F59E0B" : "#2196F3"),
          zone_type: zone.zone_type
        }));
        const filteredZones = graphQLZones.filter((zone: any) => zone.name !== "Main Yard");
        setAllZones(filteredZones);
        setAllFacilityZones(result.yard.facility_zones);
        
        // Process and store all yard spots for trailer search
        const allSpots: YardSpot[] = [];
        result.yard.facility_zones.forEach((zone: any) => {
          if (zone.yard_spots && zone.yard_spots.length > 0) {
            zone.yard_spots.forEach((spot: any) => {
              // Determine spot status
              let status: Status = 'available';
              const configStatus = spot.configuration?.status;
              if (configStatus === 'unavailable') {
                status = 'unavailable';
              } else if (spot.yard_spot_status === 'INACTIVE' || spot.yard_spot_status === 'inactive') {
                status = 'inactive';
              } else if (spot.trailer && spot.trailer.status && spot.trailer.status.name) {
                if (spot.trailer.status.name === 'LOADED') {
                  status = 'loaded';
                } else if (spot.trailer.status.name === 'EMPTY') {
                  status = 'empty';
                } else {
                  status = 'in-use';
                }
              } else if (spot.trailer_uuid) {
                status = 'in-use';
              }
              
              // Determine direction
              let direction: string | null = null;
              if (spot.shipment && spot.shipment.direction) {
                direction = spot.shipment.direction;
              }
              
              // Map to YardSpot type
              allSpots.push({
                id: spot.yard_spot_uuid,
                number: parseInt(spot.yard_spot_name.replace(/\D/g, '')) || 0,
                displayNumber: spot.yard_spot_name,
                zoneId: zone.facility_zone_uuid,
                status,
                driverId: spot.driver_id || null,
                driverName: spot.driver_name || null,
                trailerId: spot.trailer_uuid || null,
                trailerNumber: spot.trailer_name || null,
                shipmentNumber: spot.shipment_reference_number || null,
                bol_id: spot.shipment_id || null,
                carrier_name: spot.carrier_name || null,
                current_status: spot.yard_spot_status || null,
                direction,
                order_number: spot.order_number ? parseInt(spot.order_number, 10) : null,
                trailerStatus: spot.trailer?.status?.name || null,
                notes: spot.configuration?.notes || null,
                active_lineage: spot.trailer?.active_lineage ? {
                  uuid: spot.trailer.active_lineage.uuid,
                  notes: spot.trailer.active_lineage.notes,
                  created_at: spot.trailer.active_lineage.created_at
                } : undefined,
                data: spot.trailer?.data || null,
                scheduledTimes: []
              });
            });
          }
        });
        setAllYardSpots(allSpots);
      }

      // Store warehouse data
      if (result?.warehouse) {
        setWarehouseData(result.warehouse);
      }
    } catch (err) {
      console.error('Failed to fetch background data:', err);
      // Don't set error state - this is background loading
    }
  }, [selectedFacility]);

  // Warehouse-only fetch function (no yard data duplication)
  const fetchWarehouseDataOnly = useCallback(async () => {
    if (!selectedFacility) return;
    
    try {
      const result = await getWarehouseData(selectedFacility.id.toString());
      
      // Store warehouse data
      if (result?.warehouse) {
        setWarehouseData(result.warehouse);
      }
    } catch (err) {
      console.error('Failed to fetch warehouse data:', err);
    }
  }, [selectedFacility]);

  // Consolidated data fetching when facility changes
  // Always load full yard (no zone filtering) since we use zone-based parking lot view
  useEffect(() => {
    if (!selectedFacility) return;
    fetchConsolidatedData(undefined); // Always load all zones
    
    // Load warehouse data in background (avoid duplicate yard data fetch)
    setTimeout(() => fetchWarehouseDataOnly(), 100);
  }, [selectedFacility, fetchConsolidatedData, fetchWarehouseDataOnly]);

  // Create a memoized refresh function that calls the consolidated fetch
  // Always load full yard (no zone filtering) for zone-based parking lot view
  const refreshData = useCallback(async (facilityZoneUuid?: string, silent?: boolean) => {
    await fetchConsolidatedData(undefined, silent); // Always load all zones
  }, [fetchConsolidatedData]);

  // Lightweight refresh for a single zone — fetches only that zone's data and merges
  // into existing state instead of replacing everything
  const refreshZone = useCallback(async (facilityZoneUuid: string) => {
    if (!selectedFacility) return;

    try {
      const result = await getYardAndFacilityData(
        selectedFacility.id.toString(),
        facilityZoneUuid
      );

      if (result?.yard?.facility_zones?.length > 0) {
        const zoneData = result.yard.facility_zones[0];

        // Build fresh spots for this zone
        const freshSpots: YardSpot[] = [];
        if (zoneData.yard_spots) {
          zoneData.yard_spots.forEach((spot: any) => {
            let status: Status = 'available';
            const configStatus = spot.configuration?.status;
            if (configStatus === 'unavailable') {
              status = 'unavailable';
            } else if (spot.yard_spot_status === 'INACTIVE' || spot.yard_spot_status === 'inactive') {
              status = 'inactive';
            } else if (spot.trailer?.status?.name) {
              if (spot.trailer.status.name === 'LOADED') {
                status = 'loaded';
              } else if (spot.trailer.status.name === 'EMPTY') {
                status = 'empty';
              } else {
                status = 'in-use';
              }
            } else if (spot.trailer_uuid) {
              status = 'in-use';
            }

            let direction: string | null = null;
            if (spot.shipment?.direction) {
              direction = spot.shipment.direction;
            }

            freshSpots.push({
              id: spot.yard_spot_uuid,
              number: parseInt(spot.yard_spot_name.replace(/\D/g, '')) || 0,
              displayNumber: spot.yard_spot_name,
              zoneId: zoneData.facility_zone_uuid,
              status,
              driverId: spot.driver_id || null,
              driverName: spot.driver_name || null,
              trailerId: spot.trailer_uuid || null,
              trailerNumber: spot.trailer_name || null,
              shipmentNumber: spot.shipment_reference_number || null,
              bol_id: spot.shipment_id || null,
              carrier_name: spot.carrier_name || null,
              current_status: spot.yard_spot_status || null,
              direction,
              order_number: spot.order_number ? parseInt(spot.order_number, 10) : null,
              trailerStatus: spot.trailer?.status?.name || null,
              notes: spot.configuration?.notes || null,
              active_lineage: spot.trailer?.active_lineage ? {
                uuid: spot.trailer.active_lineage.uuid,
                notes: spot.trailer.active_lineage.notes,
                created_at: spot.trailer.active_lineage.created_at
              } : undefined,
              data: spot.trailer?.data || null,
              scheduledTimes: []
            });
          });
        }

        // Merge: replace spots for this zone, keep all other zones intact
        setYardSpots(prev => {
          const otherZoneSpots = prev.filter(s => s.zoneId !== facilityZoneUuid);
          const merged = [...otherZoneSpots, ...freshSpots];
          merged.sort((a, b) => {
            if (a.zoneId !== b.zoneId) return a.zoneId.localeCompare(b.zoneId);
            const aOrder = a.order_number ?? Number.MAX_SAFE_INTEGER;
            const bOrder = b.order_number ?? Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
          });
          return merged;
        });

        // Also merge into facilityZones
        setFacilityZones(prev => {
          const others = prev.filter((z: any) => z.facility_zone_uuid !== facilityZoneUuid);
          return [...others, zoneData];
        });
      }
    } catch (err) {
      console.error('Failed to refresh zone:', err);
      // Non-critical — don't set error state, caller handles its own errors
    }
  }, [selectedFacility]);

  // Helper function to find locks affecting a specific yard spot
  const getLocksForYardSpot = (yardSpotId: string) => {
    const relevantLocks = activeLocks.filter((lock: any) => {
      const lockResourceUuid = lock.resource_uuid || lock.resource?.uuid;
      
      // Check if lock directly targets this yard spot
      if (lockResourceUuid === yardSpotId) {
        console.log(`Found direct yard spot lock for ${yardSpotId}:`, lock);
        return true;
      }
      
      return false;
    });
    
    console.log(`Total locks found for yard spot ${yardSpotId}:`, relevantLocks.length, relevantLocks);
    return relevantLocks;
  };

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacilityWithStorage = (facility: { id: number; name: string } | null) => {
    setSelectedFacility(facility);
    saveFacilityToStorage(facility);
  };

  // Return the consolidated data and functionality
  return {
    loading,
    yardSpots,
    yardHistory,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility: setSelectedFacilityWithStorage,
    timeInterval,
    setTimeInterval,
    zones,
    facilityZones,
    allZones,
    allFacilityZones,
    allYardSpots,
    warehouseData,
    refreshData,
    refreshZone,
    // Task-based locks
    taskBasedLockedSpots,
    taskBasedLockedTrailers,
    activeLocks,
    allFacilityLocks,
    getLocksForYardSpot
  };
}; 